/**
 * Gaze feature extraction from MediaPipe Face Landmarker output.
 *
 * The core signal is the iris centre position normalised inside the eye socket
 * (per eye, horizontal + vertical) — this is largely invariant to where the
 * face sits in the camera frame. Head pose (yaw/pitch/roll from the facial
 * transformation matrix) plus head position (nose tip + metric distance)
 * complete the feature vector so the calibration regression can compensate
 * for head movement, which is what sinks naive approaches like WebGazer.
 */
import type { FaceLandmarkerResult, NormalizedLandmark } from '@mediapipe/tasks-vision'

// Canonical FaceMesh indices (refined landmarks / iris enabled).
// "Right eye" = image-left eye cluster, paired with iris 468; the 362/263
// cluster pairs with iris 473.
const R_IRIS = 468
const R_OUTER = 33
const R_INNER = 133
const R_TOP = 159
const R_BOTTOM = 145
const L_IRIS = 473
const L_INNER = 362
const L_OUTER = 263
const L_TOP = 386
const L_BOTTOM = 374
const NOSE_TIP = 1

export interface GazeFeatures {
  /** Base regression features (see BASE_DIM). */
  base: number[]
  /** Mean eye-openness ratio — used for blink rejection. */
  eyeOpenness: number
  /** Blendshape blink scores when available (0..1, higher = more closed). */
  blinkScore: number
}

export const BASE_DIM = 12

function dist(a: NormalizedLandmark, b: NormalizedLandmark): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.hypot(dx, dy)
}

/**
 * Per-eye iris signals.
 *
 * Horizontal: iris x between the corners (corners are gaze-invariant).
 * Vertical: iris y offset from the CORNER midline, normalised by eye width.
 * Crucially NOT normalised by the eyelids — the lids move with vertical gaze,
 * which cancels the signal and leaves the y-fit hostage to head pitch (seen as
 * a systematic downward shift in live tracking).
 * Openness: lid gap / eye width — lid droop itself carries vertical-gaze
 * information, so it's exposed as its own feature.
 */
function irisRatios(
  lm: NormalizedLandmark[],
  iris: number,
  inner: number,
  outer: number,
  top: number,
  bottom: number,
): { hx: number; vy: number; openness: number } {
  const width = lm[outer].x - lm[inner].x
  const eyeSpan = Math.max(dist(lm[inner], lm[outer]), 1e-6)
  const hx = Math.abs(width) > 1e-6 ? (lm[iris].x - lm[inner].x) / width : 0.5
  const cornerMidY = (lm[inner].y + lm[outer].y) / 2
  const vy = (lm[iris].y - cornerMidY) / eyeSpan
  const openness = dist(lm[top], lm[bottom]) / eyeSpan
  return { hx, vy, openness }
}

/** Extract yaw/pitch/roll (radians) + translation from the 4×4 column-major matrix. */
function headPose(matrix: number[]): { yaw: number; pitch: number; roll: number; tz: number } {
  // Column-major: rotation columns are m[0..2], m[4..6], m[8..10]; translation m[12..14].
  const yaw = Math.atan2(matrix[8], matrix[10])
  const pitch = Math.atan2(-matrix[9], Math.hypot(matrix[8], matrix[10]))
  const roll = Math.atan2(matrix[1], matrix[5])
  return { yaw, pitch, roll, tz: matrix[14] }
}

/**
 * Returns null when no face is present. `blinkScore` falls back to an
 * openness-derived value if blendshapes are missing.
 */
export function extractGazeFeatures(result: FaceLandmarkerResult): GazeFeatures | null {
  const lm = result.faceLandmarks?.[0]
  if (!lm || lm.length <= L_IRIS) return null

  const right = irisRatios(lm, R_IRIS, R_INNER, R_OUTER, R_TOP, R_BOTTOM)
  const left = irisRatios(lm, L_IRIS, L_INNER, L_OUTER, L_TOP, L_BOTTOM)

  const matrixData = result.facialTransformationMatrixes?.[0]?.data
  const pose = matrixData && matrixData.length >= 16
    ? headPose(Array.from(matrixData))
    : { yaw: 0, pitch: 0, roll: 0, tz: 0 }

  let blinkScore = 0
  const shapes = result.faceBlendshapes?.[0]?.categories
  if (shapes) {
    for (const s of shapes) {
      if (s.categoryName === 'eyeBlinkLeft' || s.categoryName === 'eyeBlinkRight') {
        blinkScore = Math.max(blinkScore, s.score)
      }
    }
  }

  const base = [
    left.hx,
    left.vy,
    right.hx,
    right.vy,
    left.openness,
    right.openness,
    pose.yaw,
    pose.pitch,
    pose.roll,
    lm[NOSE_TIP].x,
    lm[NOSE_TIP].y,
    pose.tz / 100, // cm → same order of magnitude as the other features
  ]

  return {
    base,
    eyeOpenness: (left.openness + right.openness) / 2,
    blinkScore,
  }
}

/**
 * Polynomial expansion of the model input: bias + input + iris quadratics +
 * iris×head-pose cross terms + luminance×eye cross terms.
 *
 * The input is the 12 face features from extractGazeFeatures PLUS a 13th
 * context feature: background luminance (0..1) of the screen the user is
 * looking at. Screen brightness drives pupil size, which systematically warps
 * landmark-based gaze estimates (the "pupil-size artifact" — RealEye calibrates
 * across light/dark/grey backgrounds for exactly this reason). Calibrating over
 * multiple luminances and giving the fit luminance cross terms keeps that
 * artifact out of the gaze coefficients.
 */
export function expandFeatures(input: number[]): number[] {
  const [lhx, lvy, rhx, rvy, lopen, ropen, yaw, pitch] = input
  const bg = input[12] ?? 0
  return [
    1,
    ...input,
    lhx * lhx,
    lvy * lvy,
    rhx * rhx,
    rvy * rvy,
    lhx * yaw,
    rhx * yaw,
    lvy * pitch,
    rvy * pitch,
    lopen * pitch,
    ropen * pitch,
    bg * lvy,
    bg * rvy,
    bg * lopen,
    bg * ropen,
  ]
}

/** BASE_DIM face features + 1 context feature (background luminance). */
export const INPUT_DIM = BASE_DIM + 1
export const EXPANDED_DIM = 1 + INPUT_DIM + 14
